/* ==========================================================================
   Incipit · Feed social — v2 (mode marquee continu)
   • Scroll natif horizontal (flèches + swipe doigt)
   • MARQUEE CONTINU : défilement fluide façon ticker, sans saut
     - Slides dupliqués à la fin pour boucler à l'infini sans coupure visible
     - Pause au hover / focus / touch / wheel
     - Pause temporaire 4s après clic sur flèche (reprise auto)
     - Respect strict de prefers-reduced-motion (pas de marquee si activé)
   • Reveal animations
   ========================================================================== */

(function () {
  'use strict';

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };


  /* ---------- SLIDER MARQUEE ----------------------------------------- */

  class IncipitSFSlider {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.slider = section.querySelector('[data-slider]');
      this.track = section.querySelector('[data-slider-track]');
      if (!this.track) return;

      this.autoplayEnabled = section.dataset.autoplay === 'true';

      // Le liquid passe autoplay_speed × 1000 (en ms). On reconvertit en
      // secondes brutes (2-10) puis on multiplie par 12 pour avoir des pixels
      // par seconde (24 → 120 px/s). Doux à très rapide.
      const autoplayMs = parseInt(section.dataset.autoplaySpeed, 10) || 5000;
      const speedSetting = autoplayMs / 1000;
      this.pixelsPerSecond = speedSetting * 12;

      this.isPaused = false;
      this.rafId = null;
      this.lastTime = 0;
      this.virtualScroll = 0;
      this.originalWidth = 0;
      this.originalSlides = [];

      this.interactionTimeout = null;

      this.prefersReducedMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.bind();

      // Marquee uniquement si autoplay ON + pas de reduced-motion
      if (this.autoplayEnabled && !this.prefersReducedMotion) {
        this.setupMarquee();
      } else {
        requestAnimationFrame(() => this.updateArrows());
      }
    }


    /* ----- Setup marquee : duplique les slides et démarre la boucle ----- */

    setupMarquee() {
      this.section.classList.add('incipit-sf--marquee');

      this.originalSlides = Array.from(this.track.children);
      if (this.originalSlides.length === 0) return;

      // Duplique chaque slide à la fin (clone pour boucle infinie)
      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', 'true');
        clone.querySelectorAll('a, button').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        clone.removeAttribute('data-reveal-card');
        clone.removeAttribute('data-reveal');
        this.track.appendChild(clone);
      });

      this.waitForImagesAndStart();
    }

    waitForImagesAndStart() {
      const images = this.track.querySelectorAll('img');
      let pending = 0;
      images.forEach((img) => {
        if (!img.complete) pending++;
      });

      if (pending === 0) {
        this.startMarquee();
        return;
      }

      images.forEach((img) => {
        if (img.complete) return;
        const done = () => {
          pending--;
          if (pending === 0) this.startMarquee();
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }

    recalculateOriginalWidth() {
      if (!this.originalSlides.length) return;
      const trackStyle = getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.gap) || parseFloat(trackStyle.columnGap) || 0;

      let total = 0;
      this.originalSlides.forEach((slide) => {
        total += slide.offsetWidth + gap;
      });
      this.originalWidth = total;
    }

    startMarquee() {
      this.recalculateOriginalWidth();
      this.virtualScroll = this.track.scrollLeft;
      this.lastTime = performance.now();
      this.tick = this.tick.bind(this);
      this.rafId = requestAnimationFrame(this.tick);
    }

    stopMarquee() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    tick(now) {
      const elapsed = now - this.lastTime;
      this.lastTime = now;

      if (!this.isPaused && this.originalWidth > 0) {
        const delta = (this.pixelsPerSecond * elapsed) / 1000;
        this.virtualScroll += delta;

        // Loop : quand on dépasse la largeur des originaux, on revient au début
        // (visuellement transparent grâce aux clones qui prennent le relais)
        if (this.virtualScroll >= this.originalWidth) {
          this.virtualScroll -= this.originalWidth;
        }

        this.track.scrollLeft = this.virtualScroll;
      }

      this.rafId = requestAnimationFrame(this.tick);
    }


    /* ----- Pause / Resume ----- */

    pause() {
      this.isPaused = true;
    }

    resume() {
      if (this.prefersReducedMotion || !this.autoplayEnabled) return;
      this.isPaused = false;
      // Sync virtualScroll avec la position actuelle (au cas où l'user a scroll manuellement)
      this.virtualScroll = this.track.scrollLeft;
      this.lastTime = performance.now();
    }

    /**
     * Pause temporaire : l'autoplay reprend après X ms d'inactivité.
     * Utilisé pour les interactions ponctuelles (clic flèche, wheel, etc.)
     */
    pauseTemporary(duration = 4000) {
      this.pause();
      if (this.interactionTimeout) {
        clearTimeout(this.interactionTimeout);
      }
      this.interactionTimeout = setTimeout(() => this.resume(), duration);
    }


    /* ----- Bindings ----- */

    bind() {
      // Update flèches au scroll (passif)
      this._onScroll = () => this.updateArrows();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // Resize : recalcul largeur
      this._onResize = debounce(() => {
        if (this.autoplayEnabled && !this.prefersReducedMotion) {
          this.recalculateOriginalWidth();
        }
        this.updateArrows();
      }, 150);
      window.addEventListener('resize', this._onResize);

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObs = new ResizeObserver(this._onResize);
        this._resizeObs.observe(this.section);
      }

      // Empêche le drag natif des images (sinon Firefox/Safari font drag image)
      this.track.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Flèches
      const prevBtns = this.section.querySelectorAll(`[data-slider-prev][data-section="${this.sectionId}"]`);
      const nextBtns = this.section.querySelectorAll(`[data-slider-next][data-section="${this.sectionId}"]`);
      prevBtns.forEach((b) => b.addEventListener('click', () => {
        this.prev();
        if (this.autoplayEnabled) this.pauseTemporary();
      }));
      nextBtns.forEach((b) => b.addEventListener('click', () => {
        this.next();
        if (this.autoplayEnabled) this.pauseTemporary();
      }));

      // Pause au hover / focus / touch / wheel
      if (this.autoplayEnabled && !this.prefersReducedMotion) {
        this.section.addEventListener('mouseenter', () => this.pause());
        this.section.addEventListener('mouseleave', () => this.resume());
        this.section.addEventListener('focusin', () => this.pause());
        this.section.addEventListener('focusout', () => this.resume());

        this.track.addEventListener('touchstart', () => this.pause(), { passive: true });
        this.track.addEventListener('touchend', () => {
          setTimeout(() => this.resume(), 800);
        }, { passive: true });
        this.track.addEventListener('touchcancel', () => {
          setTimeout(() => this.resume(), 800);
        }, { passive: true });

        let wheelTimeout = null;
        this.track.addEventListener('wheel', () => {
          this.pause();
          if (wheelTimeout) clearTimeout(wheelTimeout);
          wheelTimeout = setTimeout(() => this.resume(), 800);
        }, { passive: true });

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.pause();
          else this.resume();
        });
      }

      // Update arrows après chargement des images
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', () => this.updateArrows(), { once: true });
        }
      });
    }


    /* ----- Navigation manuelle ----- */

    next() {
      this.track.scrollBy({
        left: this.track.clientWidth * 0.9,
        behavior: 'smooth',
      });
    }

    prev() {
      this.track.scrollBy({
        left: -this.track.clientWidth * 0.9,
        behavior: 'smooth',
      });
    }

    updateArrows() {
      const prevBtns = this.section.querySelectorAll(`[data-slider-prev][data-section="${this.sectionId}"]`);
      const nextBtns = this.section.querySelectorAll(`[data-slider-next][data-section="${this.sectionId}"]`);

      // En mode marquee, les flèches restent toujours actives (boucle infinie)
      if (this.autoplayEnabled && !this.prefersReducedMotion) {
        prevBtns.forEach((b) => { b.disabled = false; });
        nextBtns.forEach((b) => { b.disabled = false; });
        return;
      }

      // Mode normal (sans autoplay) : disable selon position
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

      prevBtns.forEach((b) => { b.disabled = atStart; });
      nextBtns.forEach((b) => { b.disabled = atEnd; });
    }
  }


  /* ---------- REVEAL ANIMATIONS --------------------------------------- */

  function initReveal(section) {
    if (typeof IntersectionObserver === 'undefined') {
      section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target;
        let delay = 0;

        if (el.hasAttribute('data-reveal-card')) {
          const track = el.closest('[data-slider-track]');
          if (track) {
            // En mode marquee, fade groupé (sinon le stagger se voit sur les clones aussi)
            const isMarquee = section.classList.contains('incipit-sf--marquee');
            if (isMarquee) {
              delay = 300;
            } else {
              const cards = Array.from(track.querySelectorAll('[data-reveal-card]'));
              const idx = cards.indexOf(el);
              delay = 300 + idx * 60;
            }
          }
        } else {
          delay = parseInt(el.dataset.revealDelay, 10) || 0;
        }

        setTimeout(() => el.classList.add('is-revealed'), delay);
        observer.unobserve(el);
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -10% 0px',
    });

    section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
      observer.observe(el);
    });
  }


  /* ---------- INIT ---------------------------------------------------- */

  function initSection(section) {
    if (!section || section.dataset.incipitSfInit === 'true') return;
    section.dataset.incipitSfInit = 'true';

    new IncipitSFSlider(section);
    initReveal(section);
  }

  function initAll() {
    document.querySelectorAll('.incipit-sf').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.incipit-sf');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:reorder', initAll);
})();